import { Box, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './style'
import { useStackOneHub } from '@stackone/react-hub'
import { useTenantFormContext } from '../tenant-form-context'
import { usePromiseEvent } from '../../../hooks'
import { ActionButton } from '@emerald-works-nova/components'
import { BannerStatic } from '../../../components'
import { useDispatch } from 'react-redux'
import { TenantEditSlice } from '../../../reducers'
import { useConfirm } from 'material-ui-confirm'
import { FormInputRow, Input } from '@emerald-works-nova/components/dist/components'
import { Controller, useForm } from 'react-hook-form'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { getAttributeList } from '../components/saml-atttribute-input/cognito-atributes'
import { stackOnePreferredLanguages } from '../../../util/stackone-preferred-languages'

const LinkAccountButton = ({ onSuccess, isWorking }) => {
  const { startConnect } = useStackOneHub()
  const { tenant } = useTenantFormContext()

  const [retrievingToken, setRetrievingToken] = React.useState(false)
  const retrieveConnectSessionToken = usePromiseEvent('stackOneRetrieveConnectSessionToken')

  const startFlow = useCallback(async () => {
    try {
      setRetrievingToken(true)
      const sessionToken = await retrieveConnectSessionToken({ tenantId: tenant.pk })
      startConnect({
        sessionToken,
        onSuccess: onSuccess
      })
    } catch (err) {
      console.error(err)
      BannerStatic.show({ label: 'Failed to retrieve the connection session token, please try again later.', isError: true })
    } finally {
      setRetrievingToken(false)
    }
  }, [startConnect, retrieveConnectSessionToken, onSuccess, tenant.pk])

  const hasAccountLinked = tenant?.features?.stackOne?.accountId

  return (
    <ActionButton onClick={startFlow} isWorking={isWorking || retrievingToken}>{hasAccountLinked ? 'Update StackOne Account' : 'Connect Account'}</ActionButton>
  )
}

export const StackOneSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const { tenant } = useTenantFormContext()
  const { userPoolId } = tenant
  const confirm = useConfirm()
  const dispatch = useDispatch()
  const updateTenantAccountLink = usePromiseEvent('stackOneUpdateTenantAccountLink')
  const deleteTenantAccountLink = usePromiseEvent('stackOneDeleteTenantAccountLink')
  const populateStackOne = usePromiseEvent('stackOnePopulate')
  const stackOneFeature = tenant?.features?.stackOne
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      defaultCategory: stackOneFeature?.defaultCategory,
      ssoUserAttributeId: stackOneFeature?.ssoUserAttributeId,
      accountId: stackOneFeature?.accountId,
      provider: stackOneFeature?.provider,
      languageToDisplay: stackOneFeature?.languageToDisplay
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [cognitoAttributes, setCognitoAttributes] = useState(getAttributeList())

  const [getCustomAttributeList] = useEvent([
    {
      eventName: 'tenantGetSSOCustomAttributes',
      onSuccess: (payload) => {
        console.log('tenantGetSSOCustomAttributes', { payload })
        setCognitoAttributes([
          ...getAttributeList(),
          ...payload
        ])
      },
      onError: (err) => {
        BannerStatic.show({ label: 'Failed to retrieve custom attributes', isError: true })
        console.log('Failing to get custom attributes', err)
      }
    }
  ])

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Stackone integration settings updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the toolkit features configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const { getValues, setValue, formState: { errors, isValid } } = form

  // const [syncStatus, setSyncStatus] = useState(null)
  const [syncStatus] = useState(null)

  // const [getSyncStatus] = useEvent([{
  //   eventName: 'stackOneGetLogsForTenant',
  //   onSuccess: (payload) => {
  //     setSyncStatus(payload)
  //   },
  //   onError: (error) => {
  //     console.error('Error getting sync status', error)
  //     BannerStatic.show({ label: 'An unexpected error has happened while loading sync status', isError: true })
  //   }
  // }])

  const [updatingAccount, setUpdatingAccount] = React.useState(false)
  const [deletingAccount, setDeletingAccount] = React.useState(false)
  const [populating, setPopulating] = React.useState(false)

  useEffect(() => {
    getCustomAttributeList.trigger({ userPoolId })
  }, [getCustomAttributeList, userPoolId])

  // const handleGetSyncStatus = () => {
  //   getSyncStatus.trigger({
  //     tenantId: tenant.pk
  //   })
  // }

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        stackOne: {
          ...tenant?.features?.stackOne,
          ...getValues()
        }
      }
    })
    setIsLoading(true)
  }

  const isSaveDisabled = useMemo(() => isLoading || !isValid, [isLoading, isValid])

  const handleUpdateAccountLink = useCallback(async (account) => {
    if (tenant.features.stackOne?.accountId === account.id) {
      BannerStatic.show({ label: 'StackOne account updated successfully!' })
      return
    }

    setUpdatingAccount(true)
    try {
      const updatedTenant = await updateTenantAccountLink({
        tenantId: tenant.pk,
        account: account
      })
      dispatch(TenantEditSlice.actions.updateTenantFeatures(updatedTenant.features))

      const stackOneFeatureUpdated = updatedTenant.features.stackOne

      setValue('accountId', stackOneFeatureUpdated.accountId)
      setValue('provider', stackOneFeatureUpdated.provider)
      setValue('defaultCategory', stackOneFeatureUpdated.defaultCategory)
      setValue('ssoUserAttributeId', stackOneFeatureUpdated.ssoUserAttributeId)
      setValue('languageToDisplay', stackOneFeatureUpdated.languageToDisplay)

      BannerStatic.show({ label: 'Account linked successfully!' })
    } catch (error) {
      BannerStatic.show({ label: 'Failed to link account!', isError: true })
      return
    } finally {
      setUpdatingAccount(false)
    }
  }, [tenant.pk, tenant.features?.stackOne?.accountId, updateTenantAccountLink, setValue, dispatch])

  const handlePopulate = useCallback(async () => {
    try {
      setPopulating(true)
      await populateStackOne({
        tenantId: tenant.pk
      })
      BannerStatic.show({ label: 'StackOne starting populating successfully!' })
    } catch (err) {
      BannerStatic.show({ label: 'Failed to populate StackOne!', isError: true })
    } finally {
      setPopulating(false)
    }
  }, [tenant.pk, populateStackOne])

  const handleDeleteAccountLink = useCallback(async () => {
    confirm({
      title: 'Delete StackOne Tenant Account',
      description: 'Are you sure you want to delete the StackOne integration for this specific tenant?',
      confirmationText: 'Yes, delete it.',
      cancellationText: 'No, Thanks.',
      titleProps: {
        'data-test': 'remove-api-stackone-confirmation-title'
      },
      confirmationButtonProps: {
        'data-test': 'confirm-remove-api-stackone-button'
      },
      cancellationButtonProps: {
        'data-test': 'cancel-remove-api-stackone-button'
      }
    }).then(async () => {
      setDeletingAccount(true)
      try {
        const updatedTenant = await deleteTenantAccountLink({
          tenantId: tenant.pk
        })
        dispatch(TenantEditSlice.actions.updateTenantFeatures(updatedTenant.features))
        BannerStatic.show({ label: 'Account deleted successfully!' })
      } catch (error) {
        BannerStatic.show({ label: 'Failed to delete account!', isError: true })
        return
      } finally {
        setDeletingAccount(false)
      }
    }).catch(() => { })
  }, [tenant.pk, deleteTenantAccountLink, dispatch, confirm])

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' flexDirection='row'>
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              StackOne Integration
            </Typography>
            <Box
              display='flex'
              style={{
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                flex: 1,
                padding: spacing(1)
              }}
            >
              {!stackOneFeature && (
                <LinkAccountButton
                  onSuccess={handleUpdateAccountLink}
                  isWorking={updatingAccount}
                />
              )}
            </Box>
          </Box>
          {stackOneFeature && (
            <>
              <Divider style={{ marginBottom: spacing(1) }} />
              <Box display='flex' flexDirection='column' gap={2} justifyContent='space-between' mt={0} mb={2}>
                <Box className={classes.formCtn}>
                  <Grid item xs={12}>
                    <FormInputRow
                      title='Account Id'
                      description='StackOne account Id.'
                      smallFormTitles
                      InputComponent={
                        <Input
                          label='Account Id'
                          name='accountId'
                          control={form.control}
                          InputProps={{
                            readOnly: true
                          }}
                          disabled
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputRow
                      title='Provider'
                      description='StackOne provider.'
                      smallFormTitles
                      InputComponent={
                        <Input
                          label='Provider'
                          name='provider'
                          InputProps={{
                            readOnly: true
                          }}
                          control={form.control}
                          disabled
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputRow
                      title='Default Category Name'
                      description='Specify the default category name for the integration.'
                      smallFormTitles
                      InputComponent={
                        <Input
                          label='Default Category Name'
                          name='defaultCategory'
                          control={form.control}
                          valid={errors.defaultCategory === undefined}
                          showErrors
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputRow
                      title='SSO Atribute Identifier'
                      description='Specify the cognito attribute that we will use to identify the user in the integration LMS.'
                      smallFormTitles
                      InputComponent={
                        <Controller
                          name='ssoUserAttributeId'
                          control={form.control}
                          render={({ field }) =>
                            <FormControl sx={{ width: '100%' }} error={errors.ssoUserAttributeId !== undefined} variant='outlined' className={classes.selectControl}>
                              <InputLabel id='cognito-attribute-name-lbl'>Cognito Attribute Name</InputLabel>
                              <Select
                                variant='outlined'
                                {...field}
                                id='cognito-attribute-name'
                                labelId='cognito-attribute-name-lbl'
                                value={field.value || ''}
                                renderValue={(value) => value}
                              >
                                <MenuItem value={null} key='empty'>
                                  <Grid container direction='column'>
                                    <Typography variant='body2'>&nbsp;</Typography>
                                    <Typography gutterBottom variant='body2'>&nbsp;</Typography>
                                  </Grid>
                                </MenuItem>
                                {cognitoAttributes.map(opt => (
                                  <MenuItem value={opt.name} key={opt.name}>
                                    <Grid container direction='column'>
                                      <Typography variant='body2'>{opt.label}</Typography>
                                      <Typography gutterBottom variant='body2'>{opt.description}</Typography>
                                    </Grid>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormInputRow
                      title='LMS Language Display'
                      description='Specify the language to display on the LMS. Note: ATM, It will not translate the article, just change the article language on LMS.'
                      smallFormTitles
                      InputComponent={
                        <Controller
                          name='languageToDisplay'
                          control={form.control}
                          render={({ field }) =>
                            <FormControl sx={{ width: '100%' }} error={errors.languageToDisplay !== undefined} variant='outlined' className={classes.selectControl}>
                              <InputLabel id='language-to-display-select-lbl'>Language to display</InputLabel>
                              <Select
                                variant='outlined'
                                {...field}
                                id='language-to-display-select'
                                labelId='language-to-display-select-lbl'
                                value={field.value || ''}
                              >
                                <MenuItem value={null} key='empty'>
                                  <Typography variant='body2'>&nbsp;</Typography>
                                </MenuItem>
                                {stackOnePreferredLanguages.map(opt => (
                                  <MenuItem value={opt.value} key={opt.value}>
                                    <Typography>{opt.label} ({opt.value})</Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>}
                        />
                      }
                    />
                  </Grid>
                </Box>

                {syncStatus && (
                  <>
                    <Typography variant='body2'>StackOne Sync Status</Typography>
                    <Divider style={{ marginBottom: spacing(1) }} />
                    <Box display='flex' gap={2} flexDirection='column'>
                      <Typography variant='body2'>Pending: {syncStatus.pendingCount}</Typography>
                      <Typography variant='body2'>Success: {syncStatus.successCount}</Typography>
                      <Typography variant='body2'>Error: {syncStatus.errorCount}</Typography>
                    </Box>
                  </>
                )}
                <Box display='flex' flexDirection='row' justifyContent='flex-end' gap={2}>
                  <ActionButton
                    onClick={handlePopulate}
                    isWorking={populating}
                  > Synchronize Content To StackOne
                  </ActionButton>

                  {/*
                  // TODO: When stackone release the batchUpsert mechanism to track failures we can re-enable this feature
                  <ActionButton
                    onClick={handleGetSyncStatus}
                    isWorking={getSyncStatus.isWorking}
                  >
                    Check Sync Status
                  </ActionButton> */}

                  <LinkAccountButton
                    onSuccess={handleUpdateAccountLink}
                    isWorking={updatingAccount}
                  />

                  <ActionButton onClick={handleDeleteAccountLink} color='error' variant='error' isWorking={deletingAccount}>Delete Account</ActionButton>

                  <ActionButton
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                    variant='primary'
                  > Save
                  </ActionButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}
